import {
  Search,
  ChevronDown,
  XCircle,
  CheckCircle2,
  Loader,
  Users as UsersIcon,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useUsers } from "../../hooks/useUsers";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import ActivateModal from "../../modals/userModal/activateModal";
import DeactivateModal from "../../modals/userModal/deactivateModal";
import { useQueryClient } from "@tanstack/react-query";
import formatCurrency from "../../utils/formatCurrency";

import React, { useState } from "react";

const Users = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [statusFilter, setStatusFilter] = useState("all");
  const { users, loading, error, userStats, pagination } = useUsers(
    currentPage,
    debouncedSearch,
    statusFilter === "all" ? "" : statusFilter,
  );
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const queryClient = useQueryClient();

  const stats = [
    { label: "Total Users", value: userStats?.total_users ?? 0 },
    { label: "Active Users", value: userStats?.active_users ?? 0 },
    { label: "New This Month", value: userStats?.new_this_month_count ?? 0 },
    {
      label: "Avg Customer Value",
      value: formatCurrency(userStats?.average_users_value || 0),
    },
  ];

  return (
    <div className="min-h-screen bg-white p-4 sm:p-6 md:p-8 font-sans text-slate-700">
      {/* --- HEADER --- */}
      <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-8 gap-3">
        <div>
          <h1 className="text-lg sm:text-xl font-medium text-[#0C0C0E]">
            Users
          </h1>
          <p className="font-medium text-xs sm:text-sm text-[#7D828D]">
            Manage registered customers
          </p>
        </div>
      </div>

      {/* --- STATS CARDS --- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 mb-6 sm:mb-10 border-y border-[#EEF1F3]">
        {stats.map((stat, index) => (
          <div
            key={index}
            className={`p-4 sm:p-6 ${index !== stats.length - 1 ? "border-r border-[#EEF1F3] first:border-l-0 last:border-r-0" : ""}`}
          >
            <p className="text-[#7D828D] font-medium text-xs sm:text-sm mb-1 sm:mb-2">
              {stat.label}
            </p>
            <p className={`text-lg sm:text-2xl font-medium ${stat.color}`}>
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      <h2 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-4 text-[#0C0C0E]">
        Users List
      </h2>

      {/* --- TOOLBAR --- */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div className="relative flex-1 max-w-full sm:max-w-sm">
          <Search
            className="text-xs sm:text-sm absolute left-3 top-1/2 -translate-y-1/2 text-[#7D828D]"
            size={18}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search users by name or email..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-[#F1F2F3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] text-xs sm:text-sm"
          />
        </div>
        <div className="relative w-full sm:w-auto">
          <select
            className="appearance-none bg-[#FBF8F6] border border-[#F1F2F3] pl-4 pr-10 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D9CBBF] min-w-[120px] sm:min-w-[140px] font-medium text-slate-700 text-xs sm:text-sm w-full sm:w-auto"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            size={18}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader size={32} className="text-[#6B7143] animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-4 rounded-lg text-xs sm:text-sm">
          {error}
        </div>
      ) : users.length > 0 ? (
        <>
          {/* --- TABLE --- */}
          <div className="border border-[#EEF1F3] rounded-lg overflow-x-auto">
            <table className="min-w-[600px] w-full text-left shadow-sm text-xs sm:text-sm">
              <thead className="bg-[#FBF8F6] text-[#7D828D] font-medium">
                <tr>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">User</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Join Date</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Total Orders</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Total Spent</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Status</th>
                  <th className="px-4 sm:px-6 py-3 sm:py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF1F3]">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-[#0C0C0E]">
                          {user?.name || "Unknown"}
                        </span>
                        <span className="text-xs text-[#7D828D]">
                          {user?.email || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                      {user.created_at ? user.created_at.slice(0, 10) : ""}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-[#7D828D]">
                      {user?.orders_count || 0}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 font-medium text-[#0C0C0E]">
                      {formatCurrency(user?.total_spent || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.active_status === "active"
                            ? "bg-green-50 text-green-700 border-green-100"
                            : "bg-orange-50 text-orange-600 border-orange-100"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${user.active_status === "active" ? "bg-green-600" : "bg-orange-500"}`}
                        ></span>
                        {user.active_status}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3">
                      {/*
                      <button className="flex items-center gap-1 text-slate-700 hover:text-slate-900 font-medium text-sm active:scale-95 transition-transform duration-100">
                        <Eye size={16} /> View User
                      </button> */}
                      {user.active_status === "active" ? (
                        <button
                          className="flex items-center gap-1 text-orange-600 hover:text-orange-700 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeactivateModal(true);
                          }}
                        >
                          <XCircle size={16} /> Deactivate
                        </button>
                      ) : (
                        <button
                          className="flex items-center gap-1 text-green-600 hover:text-green-700 font-medium text-xs sm:text-sm active:scale-95 transition-transform duration-100"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowActivateModal(true);
                          }}
                        >
                          <CheckCircle2 size={16} /> Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* --- PAGINATION --- */}
          {pagination && (
            <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:gap-0 items-center justify-between border-t border-[#EEF1F3] bg-[#FBF8F6] w-full overflow-x-auto min-w-0">
              <span className="text-xs sm:text-sm text-[#7D828D]">
                Showing{" "}
                {pagination.current_page
                  ? (pagination.current_page - 1) * pagination.per_page + 1
                  : 0}{" "}
                to{" "}
                {Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total,
                )}{" "}
                of {pagination.total} users
              </span>
              <div className="w-full sm:w-auto">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4 sm:px-6 border border-[#EEF1F3] rounded-lg">
          <div className="p-1 border border-slate-300 bg-[#F8F5F1] rounded-full overflow-hidden mb-4">
            <UsersIcon size={32} className="text-[#BFAC93]" />
          </div>
          <h4 className="font-medium text-sm sm:text-base text-[#0C0C0E] text-center mb-2">
            No users yet
          </h4>
          <p className="text-xs sm:text-sm text-slate-400 text-center">
            Once customers create accounts or place orders, you'll be able to
            manage them here.
          </p>
        </div>
      )}
      {/* --- ACTIVATE MODAL --- */}
      <ActivateModal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        user={selectedUser}
        onActivateSuccess={() => {
          setShowActivateModal(false);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        }}
      />
      {/* --- DEACTIVATE MODAL --- */}
      <DeactivateModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        user={selectedUser}
        onDeactivateSuccess={() => {
          setShowDeactivateModal(false);
          queryClient.invalidateQueries({ queryKey: ["users"] });
        }}
      />
    </div>
  );
};

export default Users;
